import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
// Clock / Truck: icon của 2 tab đang tạm ẩn (FD-372) — import lại khi bỏ ẩn.
import { ArrowLeft, Zap } from 'lucide-react'

import type { Product } from '@/lib/api/product-api'
import type { OrderTab, PosOrderItem } from '@/components/pos/pos-types'
import type { PackagingDraftItem } from '@/lib/packaging-draft'
import type { PosCustomerSearchHandle } from '@/components/pos/pos-customer-search'
import type { PosQuickCheckoutHandle } from '@/components/pos/pos-quick-checkout-panel'
import type { PosDeliveryCheckoutHandle } from '@/components/pos/pos-delivery-checkout-panel'

import type { PickedPackagingMaterial } from '@/components/packaging/packaging-material-picker'
import { discountRuleApi } from '@/lib/api/discount-rule-api'
import { useAuth } from '@/hooks/use-auth'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import { usePosDisplaySync } from '@/hooks/use-pos-display-sync'
import { formatVND } from '@/lib/format'
import { formatApiError } from '@/lib/format-api-error'
import { productUnitPriceFor } from '@/lib/material-split'
import { PosOrderTabs } from '@/components/pos/pos-order-tabs'
import {
    posMaterialEffective,
    posOrderTotals,
} from '@/components/pos/pos-order-payload'
import { PosProductGrid } from '@/components/pos/pos-product-grid'
import { PosOrderItems } from '@/components/pos/pos-order-items'
import { PackagingMaterialPicker } from '@/components/packaging/packaging-material-picker'
import { buildManualRow } from '@/lib/packaging-draft'
import { newMaterialRowId, rowKeyOf } from '@/lib/material-row-id'
import { useOrderPackagingSync } from '@/hooks/use-order-packaging-sync'
import { PosCustomerSearch } from '@/components/pos/pos-customer-search'
import { PosCheckoutModal } from '@/components/pos/pos-checkout-modal'
import { PosQuickCheckoutPanel } from '@/components/pos/pos-quick-checkout-panel'
import { PosDeliveryCheckoutPanel } from '@/components/pos/pos-delivery-checkout-panel'
import { PosDisplayPicker } from '@/components/pos/pos-display-picker'
import { PosBarcodeNotFoundDialog } from '@/components/pos/pos-barcode-not-found-dialog'
import { BarcodeProductSearch } from '@/components/shared/barcode-product-search'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/sale/')({
    component: SalePage,
})

export function SalePage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const tabCounterRef = useRef(1)

    function createEmptyTab(): OrderTab {
        const id = crypto.randomUUID()
        const label = m.pos_invoice_tab({
            number: String(tabCounterRef.current++),
        })
        return { id, label, items: [], customer: null, note: '' }
    }

    // Multi-tab state
    const [tabs, setTabs] = useState<OrderTab[]>(() => [createEmptyTab()])
    const [activeTabId, setActiveTabId] = useState(() => tabs[0].id)

    // Sale mode state
    const [saleMode, setSaleMode] = useState<'quick' | 'normal' | 'delivery'>(
        'quick',
    )

    // Refs
    const productSearchRef = useRef<HTMLInputElement>(null)
    const customerSearchRef = useRef<PosCustomerSearchHandle>(null)
    const quickCheckoutRef = useRef<PosQuickCheckoutHandle>(null)
    const deliveryCheckoutRef = useRef<PosDeliveryCheckoutHandle>(null)
    // Per-tab discount rule maps: Map<tabId, Map<productId, discountPercent>>
    const ruleMapsByTabRef = useRef<Map<string, Map<string, number>>>(new Map())
    // Barcode not-found dialog state
    const [notFoundBarcode, setNotFoundBarcode] = useState('')
    const [notFoundOpen, setNotFoundOpen] = useState(false)

    const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0]
    const { data: invoiceSettings } = useInvoiceSettings()
    const vatPercent = invoiceSettings?.vat_percent ?? 0

    // Sync active cart to paired customer display (debounced)
    const displaySync = usePosDisplaySync(activeTab)

    // Update active tab helper
    const updateActiveTab = useCallback(
        (updater: (tab: OrderTab) => OrderTab) => {
            setTabs((prev) =>
                prev.map((t) => (t.id === activeTabId ? updater(t) : t)),
            )
        },
        [activeTabId],
    )

    // Product → add to order (supports quantity for barcode scanner)
    const addProduct = useCallback(
        (product: Product, qty = 1) => {
            updateActiveTab((tab) => {
                const existing = tab.items.findIndex(
                    (i) => i.product_id === product.id,
                )
                if (existing >= 0) {
                    const items = [...tab.items]
                    items[existing] = {
                        ...items[existing],
                        quantity: items[existing].quantity + qty,
                    }
                    return { ...tab, items }
                }
                // Resolve discount: product-specific rule first, then group default
                const ruleMap = ruleMapsByTabRef.current.get(tab.id)
                const productRuleDiscount = ruleMap?.get(product.id)
                const groupDiscount =
                    tab.customer?.customer_group?.discount_percent ?? 0
                const discount = productRuleDiscount ?? groupDiscount
                const newItem: PosOrderItem = {
                    product_id: product.id,
                    product_name: product.name,
                    sku: product.sku,
                    quantity: qty,
                    unit: product.unit,
                    // Mặc định GỘP: đơn giá = master (đã gồm giá vật tư). Chốt cờ +
                    // M + master để nút tách/gộp + reset-from-master hoạt động.
                    unit_price: product.unit_price,
                    discount_percent: discount,
                    material_price_split: false,
                    price_includes_material:
                        product.price_includes_material ?? false,
                    material_price_per_unit:
                        product.material_price_per_unit ?? 0,
                    master_unit_price: product.unit_price,
                    image_url: (
                        product.images?.find((img) => img.is_primary) ??
                        product.images?.[0]
                    )?.image_url,
                }
                return { ...tab, items: [...tab.items, newItem] }
            })
        },
        [updateActiveTab],
    )

    // Toggle tách/gộp giá vật tư dòng POS — RESET đơn giá SP từ master (bỏ qua
    // giá user sửa tay). Cập nhật cờ để inline vật tư đổi giá hiệu lực (0 ↔ cấu hình).
    const toggleSplit = useCallback(
        (index: number) => {
            updateActiveTab((tab) => {
                const items = [...tab.items]
                const it = items[index]
                if (!it) return tab
                const next = !(it.material_price_split ?? false)
                const master = it.master_unit_price ?? it.unit_price
                const perUnit = it.material_price_per_unit ?? 0
                items[index] = {
                    ...it,
                    material_price_split: next,
                    unit_price: productUnitPriceFor(master, perUnit, next),
                }
                return { ...tab, items }
            })
        },
        [updateActiveTab],
    )

    const handleBarcodeNotFound = useCallback((barcode: string) => {
        setNotFoundBarcode(barcode)
        setNotFoundOpen(true)
    }, [])

    const updateItem = useCallback(
        (
            index: number,
            field: 'quantity' | 'unit_price' | 'discount_percent',
            value: number,
        ) => {
            updateActiveTab((tab) => {
                const items = [...tab.items]
                items[index] = { ...items[index], [field]: value }
                return { ...tab, items }
            })
        },
        [updateActiveTab],
    )

    const removeItem = useCallback(
        (index: number) => {
            updateActiveTab((tab) => {
                const removed = tab.items[index]
                return {
                    ...tab,
                    items: tab.items.filter((_, i) => i !== index),
                    // Bỏ luôn vật tư inline gắn với SP vừa xoá (line_key =
                    // product_id) — tránh vật tư "mồ côi" vẫn tính tiền/trừ kho
                    // trong khoảng 500ms trước khi editor refetch dọn.
                    packagingItems: (tab.packagingItems ?? []).filter(
                        (p) =>
                            !p.line_key || p.line_key !== removed?.product_id,
                    ),
                }
            })
        },
        [updateActiveTab],
    )

    // Máy POS cũng bỏ tự sinh vật tư theo định mức (#M-16) — xem
    // use-order-packaging-sync.
    const { availableByMaterial } = useOrderPackagingSync({
        value: activeTab?.packagingItems ?? [],
        onChange: (packagingItems) =>
            updateActiveTab((t) => ({ ...t, packagingItems })),
    })

    const handleAddMaterialRow = useCallback(
        (mat: PickedPackagingMaterial) =>
            updateActiveTab((t) => ({
                ...t,
                packagingItems: [
                    ...(t.packagingItems ?? []),
                    buildManualRow(
                        t.packagingItems ?? [],
                        mat,
                        availableByMaterial,
                        newMaterialRowId(),
                    ),
                ],
            })),
        [updateActiveTab, availableByMaterial],
    )

    // Vật tư CÓ GIÁ render inline dưới sản phẩm (line_key = product_id vì POS gộp
    // trùng sản phẩm về 1 dòng). Sửa SL/đơn giá/CK/xoá cho vật tư thường; vật tư
    // và mọi dòng đều sửa được SL/giá.
    const patchMaterial = useCallback(
        (
            rowKey: string,
            patch: (p: PackagingDraftItem) => PackagingDraftItem,
        ) => {
            updateActiveTab((tab) => ({
                ...tab,
                // Khớp theo KHOÁ DÒNG: hai dòng thêm tay cùng vật tư đều có
                // line_key rỗng nên (material_id, line_key) không phân biệt được.
                packagingItems: (tab.packagingItems ?? []).map((p) =>
                    rowKeyOf(p) === rowKey ? patch(p) : p,
                ),
            }))
        },
        [updateActiveTab],
    )

    const handleMaterialQtyChange = useCallback(
        (rowKey: string, _lineKey: string, value: string) => {
            const qty = Math.max(0, Number(value) || 0)
            patchMaterial(rowKey, (p) => ({ ...p, quantity: qty }))
        },
        [patchMaterial],
    )

    const handleMaterialPriceChange = useCallback(
        (rowKey: string, _lineKey: string, value: string) => {
            const price = Math.max(0, Number(value) || 0)
            patchMaterial(rowKey, (p) => ({ ...p, unit_price: price }))
        },
        [patchMaterial],
    )

    const handleMaterialDiscountChange = useCallback(
        (rowKey: string, _lineKey: string, value: string) => {
            const pct = Math.min(100, Math.max(0, Number(value) || 0))
            patchMaterial(rowKey, (p) => ({ ...p, discount_percent: pct }))
        },
        [patchMaterial],
    )

    const handleMaterialRemove = useCallback(
        (rowKey: string, _lineKey: string) => {
            updateActiveTab((tab) => ({
                ...tab,
                packagingItems: (tab.packagingItems ?? []).filter(
                    (p) => rowKeyOf(p) !== rowKey,
                ),
            }))
        },
        [updateActiveTab],
    )

    // Tab management
    const addTab = useCallback(() => {
        const newTab = createEmptyTab()
        setTabs((prev) => [...prev, newTab])
        setActiveTabId(newTab.id)
    }, [])

    const closeTab = useCallback(
        (id: string) => {
            const tab = tabs.find((t) => t.id === id)
            if (tab && tab.items.length > 0 && !confirm(m.pos_close_confirm()))
                return
            setTabs((prev) => {
                const next = prev.filter((t) => t.id !== id)
                if (next.length === 0) {
                    const fresh = createEmptyTab()
                    setActiveTabId(fresh.id)
                    return [fresh]
                }
                if (activeTabId === id) {
                    setActiveTabId(next[0].id)
                }
                return next
            })
        },
        [tabs, activeTabId],
    )

    // Checkout modal state
    const [checkoutOpen, setCheckoutOpen] = useState(false)
    const inlineWorkflow = useWorkflowTracking(
        saleMode === 'delivery'
            ? 'pos_delivery_checkout'
            : 'pos_quick_checkout',
        {
            active: saleMode !== 'normal',
            attemptKey: activeTab.id,
            retainAttempts: true,
            retainedKeys: tabs.map((tab) => tab.id),
        },
    )

    // Bán được khi có sản phẩm HOẶC vật tư có giá (bán túi/hộp không kèm chè).
    const hasSellable =
        activeTab.items.length > 0 ||
        posMaterialEffective(activeTab.items, activeTab.packagingItems).total >
            0

    const handleCheckoutSuccess = useCallback(() => {
        if (displaySync.isCheckout()) displaySync.skipNextClear()
        updateActiveTab((t) => ({
            ...t,
            items: [],
            // Vật tư thêm tay (line_key rỗng) không tự biến mất khi items rỗng
            // → phải xoá tường minh, nếu không đơn chỉ vật tư vừa bán còn
            // nằm lại trong giỏ của lần bán sau.
            packagingItems: [],
            customer: null,
            note: '',
            // Chiết khấu đơn không được "dính" sang khách sau.
            orderDiscountPercent: 0,
        }))
        // Auto-focus product search for next transaction
        setTimeout(() => productSearchRef.current?.focus(), 100)
    }, [updateActiveTab, displaySync])

    // Auto-focus product search on mount and tab switch (like KiotViet)
    useEffect(() => {
        productSearchRef.current?.focus()
    }, [activeTabId])

    // Keyboard shortcuts (F3, F4, F9, Escape) + global barcode capture
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'F3') {
                e.preventDefault()
                productSearchRef.current?.focus()
            } else if (e.key === 'F4') {
                e.preventDefault()
                customerSearchRef.current?.focus()
            } else if (e.key === 'F9') {
                e.preventDefault()
                if (!hasSellable) return
                if (saleMode === 'quick') {
                    quickCheckoutRef.current?.submit()
                } else if (saleMode === 'delivery') {
                    deliveryCheckoutRef.current?.submit()
                } else {
                    setCheckoutOpen(true)
                }
            } else if (e.key === 'Escape') {
                ;(document.activeElement as HTMLElement)?.blur()
            }

            // Redirect printable keystrokes to search input when typing outside it
            const target = e.target as HTMLElement
            const isInput =
                target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'
            if (
                !isInput &&
                e.key.length === 1 &&
                !e.ctrlKey &&
                !e.metaKey &&
                !e.altKey
            ) {
                productSearchRef.current?.focus()
            }
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [saleMode, hasSellable])

    // _authed beforeLoad guarantees user exists, but useQuery may lag one render cycle
    if (!user) return null

    const totalAmount = activeTab.items.reduce(
        (s, i) => s + i.quantity * i.unit_price,
        0,
    )
    // Dòng tổng bên trái hiện tổng khách phải trả của đơn (đã chiết khấu, vật
    // tư, VAT) — cùng nguồn tính với panel Bán nhanh, không trừ số dư khách.
    // Chỉ Bán nhanh có chiết khấu đơn theo tab; 2 chế độ đang ẩn giữ chiết
    // khấu riêng trong panel nên giữ tổng tiền hàng để tránh lệch số.
    const quickPayable = (() => {
        if (saleMode !== 'quick') return undefined
        const totals = posOrderTotals(
            activeTab.items,
            activeTab.packagingItems,
            activeTab.orderDiscountPercent ?? 0,
            vatPercent,
        )
        return {
            total: totals.totalAmount,
            vatPercent,
            vatAmount: totals.vatAmount,
        }
    })()

    return (
        <div className="h-screen flex flex-col bg-background">
            {/* Top bar */}
            <div className="h-12 bg-primary flex items-center gap-2 px-3 shrink-0">
                {/* Back button */}
                <button
                    type="button"
                    onClick={() => navigate({ to: '/' })}
                    className="text-primary-foreground/80 hover:text-primary-foreground p-1"
                >
                    <ArrowLeft className="size-4" />
                </button>

                {/* Product search with barcode scanner */}
                <BarcodeProductSearch
                    workflow={inlineWorkflow}
                    onSearchInput={() => inlineWorkflow.start()}
                    variant="topbar"
                    openOnFocus
                    className="w-72"
                    inputRef={productSearchRef}
                    onProductFound={addProduct}
                    onBarcodeNotFound={handleBarcodeNotFound}
                />

                {/* Order tabs */}
                <PosOrderTabs
                    tabs={tabs}
                    activeTabId={activeTabId}
                    onSwitch={setActiveTabId}
                    onAdd={addTab}
                    onClose={closeTab}
                />

                <div className="flex-1" />
                <PosDisplayPicker />
                {displaySync.status === 'error' && (
                    <button
                        type="button"
                        onClick={() => void displaySync.retry()}
                        className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-900"
                        title={
                            formatApiError(
                                displaySync.error,
                                m.pos_display_cart_sync_failed(),
                            ).title
                        }
                    >
                        {m.pos_display_sync_retry()}
                    </button>
                )}
                <span className="text-primary-foreground/80 text-xs">
                    {user.username}
                </span>
            </div>

            {/* Main area: left (order items + packaging) + right (customer + products) */}
            <div className="flex-1 flex min-h-0">
                {/* Left panel — Order items + packaging */}
                <div className="flex-[3] flex flex-col border-r min-w-0 overflow-y-auto">
                    <PosOrderItems
                        items={activeTab.items}
                        orderNote={activeTab.note}
                        packagingItems={activeTab.packagingItems ?? []}
                        payable={quickPayable}
                        onUpdateItem={updateItem}
                        onToggleSplit={toggleSplit}
                        onRemoveItem={removeItem}
                        onNoteChange={(note) =>
                            updateActiveTab((t) => ({ ...t, note }))
                        }
                        onMaterialQtyChange={handleMaterialQtyChange}
                        onMaterialPriceChange={handleMaterialPriceChange}
                        onMaterialDiscountChange={handleMaterialDiscountChange}
                        onMaterialRemove={handleMaterialRemove}
                    />

                    {/* Phase 4 — vật tư bao bì cần dùng cho đơn POS. Luôn hiện
                        để bán được đơn chỉ vật tư (túi/hộp không kèm chè). */}
                    {
                        <div className="p-3 border-t space-y-2">
                            <div>
                                <h3 className="text-sm font-semibold">
                                    {m.packaging_editor_title()}
                                </h3>
                                <p className="text-xs text-muted-foreground">
                                    {m.packaging_editor_subtitle()}
                                </p>
                            </div>
                            {/* Vật tư render trong danh sách hàng: theo định
                                mức nằm dưới sản phẩm, tự thêm ngang hàng. */}
                            <PackagingMaterialPicker
                                onPick={handleAddMaterialRow}
                                placeholder={m.packaging_add_material()}
                            />
                        </div>
                    }
                </div>

                {/* Right panel — changes based on sale mode */}
                <div className="flex-[2] flex flex-col min-w-0 bg-muted/20">
                    {/* Customer search (shared across modes) */}
                    <div className="p-2 border-b shrink-0">
                        <PosCustomerSearch
                            ref={customerSearchRef}
                            value={activeTab.customer}
                            onChange={(customer) => {
                                // Fetch discount rules for the customer's group
                                const groupId = customer?.customer_group?.id
                                if (groupId) {
                                    discountRuleApi
                                        .list(groupId)
                                        .then((res) => {
                                            const rules = res.data ?? []
                                            const map = new Map(
                                                rules.map((r) => [
                                                    r.product_id,
                                                    r.discount_percent,
                                                ]),
                                            )
                                            ruleMapsByTabRef.current.set(
                                                activeTabId,
                                                map,
                                            )
                                        })
                                        .catch(() => {
                                            // silently ignore — will fall back to group default
                                        })
                                } else {
                                    ruleMapsByTabRef.current.delete(activeTabId)
                                }

                                updateActiveTab((t) => {
                                    const groupDiscount =
                                        customer?.customer_group
                                            ?.discount_percent ?? 0
                                    const items = t.items.map((item) => ({
                                        ...item,
                                        discount_percent: groupDiscount,
                                    }))
                                    return { ...t, customer, items }
                                })
                            }}
                        />
                    </div>

                    {saleMode === 'quick' ? (
                        /* Quick mode: inline checkout panel */
                        <PosQuickCheckoutPanel
                            ref={quickCheckoutRef}
                            workflow={inlineWorkflow}
                            tab={activeTab}
                            onSuccess={handleCheckoutSuccess}
                            onOrderDiscountChange={(percent) =>
                                updateActiveTab((t) => ({
                                    ...t,
                                    orderDiscountPercent: percent,
                                }))
                            }
                        />
                    ) : saleMode === 'delivery' ? (
                        /* Delivery mode: COD order → delivery pipeline (no payment) */
                        <PosDeliveryCheckoutPanel
                            ref={deliveryCheckoutRef}
                            workflow={inlineWorkflow}
                            tab={activeTab}
                            onSuccess={handleCheckoutSuccess}
                        />
                    ) : (
                        /* Normal mode: product grid + checkout button → modal */
                        <>
                            <PosProductGrid
                                searchTerm=""
                                onProductClick={addProduct}
                            />
                            <div className="p-2 border-t shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setCheckoutOpen(true)}
                                    disabled={!hasSellable}
                                    className="w-full rounded bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground font-semibold py-3 text-sm transition-colors"
                                >
                                    {m.pos_place_order({
                                        amount: formatVND(totalAmount),
                                    })}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Checkout modal (normal mode only) */}
            {saleMode === 'normal' && (
                <PosCheckoutModal
                    open={checkoutOpen}
                    onOpenChange={setCheckoutOpen}
                    tab={activeTab}
                    onSuccess={handleCheckoutSuccess}
                />
            )}

            {/* Barcode not found dialog */}
            <PosBarcodeNotFoundDialog
                open={notFoundOpen}
                onOpenChange={setNotFoundOpen}
                barcode={notFoundBarcode}
                onProductCreated={(product) => {
                    addProduct(product, 1)
                }}
            />

            {/* Bottom bar — Sales mode tabs.
                FD-372 (06/09/2026): quầy chỉ dùng "Bán nhanh". Hai lối vào
                "Bán thường" và "Bán giao hàng" TẠM ẨN theo yêu cầu của khầy
                Long; đơn giao hàng tạo ở màn tạo đơn thường.
                Component, state `saleMode` và cả hai nhánh render giữ NGUYÊN —
                bỏ ẩn khối này là chạy lại ngay, không phải dựng lại gì.
                Lưu ý khi bỏ ẩn: lưới sản phẩm bấm chọn nằm trong nhánh
                'normal', nên ẩn tab đó cũng là ẩn luôn lưới. */}
            <div className="h-10 border-t bg-card flex items-center justify-center gap-6 shrink-0">
                <button
                    type="button"
                    onClick={() => setSaleMode('quick')}
                    className={`flex items-center gap-1.5 text-xs pb-0.5 ${
                        saleMode === 'quick'
                            ? 'text-primary font-medium border-b-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground'
                    }`}
                >
                    <Zap className="size-3.5" />
                    {m.pos_quick_sale()}
                </button>
            </div>
        </div>
    )
}
